import {
  ComputerDesktopIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  MoonIcon,
  ChevronRightIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { imgrepeat } from "../../image";
import { useSelector } from "react-redux";
import { APP_VERSION } from "../../Localstorage/localstorage";
import moment from "moment";

const calendly = {
  name: "Calendly",
  href: "/calendly",
  img: imgrepeat.calendly,
  current: true,
};

const SIDEBAR_CONTAINER = "border-r border-[#2a3942] bg-[#111b21]";
const SIDEBAR_ACTIVE = "bg-[#2a3942] text-[#e9edef] ring-1 ring-[#00a884]/30";
const SIDEBAR_INACTIVE = "text-[#8696a0] hover:bg-[#2a3942] hover:text-[#e9edef]";
const SIDEBAR_CAL_ACTIVE = "bg-[#005c4b] text-[#e9edef]";
const SIDEBAR_CAL_INACTIVE = "text-[#8696a0] hover:bg-[#2a3942] hover:text-[#e9edef]";
const SIDEBAR_CONTAINER_ADMIN_LIGHT = "border-r border-[#3a467a] bg-[#262f5e]";
const SIDEBAR_ACTIVE_ADMIN_LIGHT = "bg-white/20 text-white ring-1 ring-white/35";
const SIDEBAR_INACTIVE_ADMIN_LIGHT = "text-[#d7dcef] hover:bg-white/10 hover:text-white";
const SIDEBAR_CAL_ACTIVE_ADMIN_LIGHT = "bg-[#2f3658] text-white";
const SIDEBAR_CAL_INACTIVE_ADMIN_LIGHT =
  "text-[#d7dcef] hover:bg-white/10 hover:text-white";
const THEME_STORAGE_KEY = "rp-theme";
const SideBar = ({
  navigation,
  classNames,
  location,
  user,
  toggleOpen,
  open,
  sidebarCollapsed = false,
  setSidebarCollapsed,
}) => {
  const [showDetails, setshowDetails] = useState(false);
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem(THEME_STORAGE_KEY) || "auto"
  );
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );
  const { mailRooms } = useSelector((state) => state.mail);
  const isAdmin = Number(user?.role) === 1;
  const isAdminLight = isAdmin && !isDark;

  const getAutoDarkState = () => {
    const hour = new Date().getHours();
    return !(hour >= 6 && hour < 19);
  };

  const applyTheme = (mode) => {
    const shouldDark =
      mode === "dark" ? true : mode === "light" ? false : getAutoDarkState();
    document.documentElement.classList.toggle("dark", shouldDark);
    document.documentElement.style.colorScheme = shouldDark ? "dark" : "light";
    setIsDark(shouldDark);
  };

  const onThemeChange = (mode) => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    setThemeMode(mode);
    applyTheme(mode);
  };

  useEffect(() => {
    applyTheme(themeMode);
    if (themeMode !== "auto") return undefined;

    const timer = setInterval(() => {
      applyTheme("auto");
    }, 60000);
    return () => clearInterval(timer);
  }, [themeMode]);

  useEffect(() => {
    const syncThemeMode = () =>
      setThemeMode(localStorage.getItem(THEME_STORAGE_KEY) || "auto");
    window.addEventListener("storage", syncThemeMode);
    return () => window.removeEventListener("storage", syncThemeMode);
  }, []);

  useEffect(() => {
    if (sidebarCollapsed) setshowDetails(false);
  }, [sidebarCollapsed]);

  const image = useMemo(() => {
    const hour = moment().hour();
    // Daytime: 8:00 to 18:59 (8-18) - white image
    return "https://www.repeathealth.com/wp-content/uploads/2024/02/Tag-Repeat-RGB-wit.png";
  }, []);

  const unreadMailCount = useMemo(
    () => (mailRooms || []).reduce((sum, item) => sum + Number(item?.sum_status || 0), 0),
    [mailRooms]
  );

  const ThemeIcon =
    themeMode === "light"
      ? SunIcon
      : themeMode === "dark"
      ? MoonIcon
      : ComputerDesktopIcon;
  return (
    <div
      className={classNames(
        "hidden grow lg:fixed lg:inset-y-0 lg:left-0 lg:z-10 lg:block lg:flex-1 lg:w-full lg:overflow-y-auto lg:pb-4",
        sidebarCollapsed ? "lg:max-w-[6.5rem] lg:px-2" : "lg:max-w-xs lg:px-6",
        isAdminLight ? SIDEBAR_CONTAINER_ADMIN_LIGHT : SIDEBAR_CONTAINER
      )}
    >
      <div
        className={classNames(
          "flex h-16 shrink-0 items-center",
          "justify-center"
        )}
      >
        <img
          className="h-10 w-auto"
          src={image}
          alt="Repeathealth"
        />
      </div>
      <nav className="flex flex-col">
        <ul role="list" className="flex flex-col gap-y-7">
          <li>
            <ul className="-mx-2 flex-1 space-y-1">
              {navigation(user, unreadMailCount).map((item) => (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      classNames(
                        isActive || location.pathname.startsWith(item.href)
                          ? isAdminLight
                            ? SIDEBAR_ACTIVE_ADMIN_LIGHT
                            : SIDEBAR_ACTIVE
                          : isAdminLight
                          ? SIDEBAR_INACTIVE_ADMIN_LIGHT
                          : SIDEBAR_INACTIVE,
                        sidebarCollapsed
                          ? "group flex items-center justify-center rounded-lg p-2.5 text-[15px] leading-6 font-semibold"
                          : "group flex gap-x-3 rounded-lg px-3 py-2.5 text-[15px] leading-6 font-semibold"
                      )
                    }
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    {item.href == "/mailbox" ? (
                      <div
                        className={classNames(
                          "relative",
                          sidebarCollapsed
                            ? "flex items-center justify-center"
                            : "flex flex-1 gap-x-3"
                        )}
                      >
                        {item?.count > 0 && <div className="bg-red-900 px-1.5 py-0.5 text-xs font-medium text-red-200 rounded absolute  -top-2 -left-3 p-1">
                          {item?.count}
                        </div>}

                        <item.icon
                          className="h-6 w-6 shrink-0"
                          aria-hidden="true"
                        />
                        {!sidebarCollapsed ? (
                          <span className="relative">{item.name}</span>
                        ) : null}
                      </div>
                    ) : (
                      <div
                        className={classNames(
                          sidebarCollapsed
                            ? "flex items-center justify-center"
                            : "flex flex-1 gap-x-3"
                        )}
                      >
                        <item.icon
                          className="h-6 w-6 shrink-0"
                          aria-hidden="true"
                        />
                        {!sidebarCollapsed ? item.name : null}
                      </div>
                    )}
                    {item?.children && !sidebarCollapsed && (
                      <>
                        {showDetails ? (
                          <button
                            title="Sluit de details"
                            className="  p-1 rounded-full"
                            onClick={() => setshowDetails(false)}
                          >
                            {" "}
                            <ChevronDownIcon className="h-4 w-4 shrink-0" />{" "}
                          </button>
                        ) : (
                          <button
                            title="Open voor details"
                            className=" p-1 rounded-full"
                            onClick={() => setshowDetails(true)}
                          >
                            {" "}
                            <ChevronRightIcon className="h-4 w-4 shrink-0" />{" "}
                          </button>
                        )}
                      </>
                    )}
                  </NavLink>
                  {item?.children && showDetails && (
                    <ul className="ml-4 space-y-1">
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <NavLink
                            to={child.href}
                            className={({ isActive }) =>
                              classNames(
                                isActive
                                  ? isAdminLight
                                    ? SIDEBAR_ACTIVE_ADMIN_LIGHT
                                    : SIDEBAR_ACTIVE
                                  : isAdminLight
                                  ? SIDEBAR_INACTIVE_ADMIN_LIGHT
                                  : SIDEBAR_INACTIVE,
                                "group flex gap-x-3 rounded-lg px-3 py-2.5 text-[15px] leading-6 font-semibold"
                              )
                            }
                          >
                            <child.icon
                              className="h-6 w-6 shrink-0"
                              aria-hidden="true"
                            />
                            {child.name}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </li>
          {user?.role == 1 && (
            <>
{/*             <li className="-mx-6 mt-auto">
              <NavLink
                to={calendly.href}
                className={({ isActive }) =>
                  classNames(
                    isActive
                      ? "bg-blue-900 text-white"
                      : "text-gray-400 hover:text-white hover:bg-blue-800",
                    "flex items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-white hover:bg-gray-900"
                  )
                }
              >
                <img
                  alt="Calendly"
                  src={imgrepeat.calendly}
                  className="size-5 rounded-full bg-indigo-700"
                />
                <span className="sr-only">Calendly</span>
                <span aria-hidden="true">Boekingen via Calendly</span>
              </NavLink>
            </li> */}
            <li className="-mx-6 mt-auto">
            <NavLink
              to={calendly.href}
              className={({ isActive }) =>
                classNames(
                  isActive
                    ? isAdminLight
                      ? SIDEBAR_CAL_ACTIVE_ADMIN_LIGHT
                      : SIDEBAR_CAL_ACTIVE
                    : isAdminLight
                    ? SIDEBAR_CAL_INACTIVE_ADMIN_LIGHT
                    : SIDEBAR_CAL_INACTIVE,
                  sidebarCollapsed
                    ? "flex items-center justify-center px-3 py-3 text-sm/6 font-semibold"
                    : "flex items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold"
                )
              }
              title={sidebarCollapsed ? "Afspraken gemaakt in Calendly" : undefined}
            >
              <img
                alt="Calendly"
                src={imgrepeat.calendly}
                className="size-5 rounded-full bg-indigo-700"
              />
              <span className="sr-only">Calendly</span>
              {!sidebarCollapsed ? (
                <span aria-hidden="true">Afspraken gemaakt in Calendly</span>
              ) : null}
            </NavLink>
          </li>
          </>
          )}
        </ul>
        {!sidebarCollapsed ? (
          <div
            className={classNames(
              "mt-2 rounded-xl border px-2.5 py-2",
              isAdminLight ? "border-white/25 bg-white/5" : "border-[#2a3942] bg-[#202c33]"
            )}
          >
            <label className="flex items-center gap-2">
              <ThemeIcon
                className={classNames(
                  "h-4 w-4 shrink-0",
                  isAdminLight ? "text-[#d7dcef]" : "text-[#8696a0]"
                )}
                aria-hidden="true"
              />
              <span
                className={classNames(
                  "text-xs font-semibold uppercase tracking-wide",
                  isAdminLight ? "text-[#d7dcef]" : "text-[#8696a0]"
                )}
              >
                Thema
              </span>
              <select
                value={themeMode}
                onChange={(event) => onThemeChange(event.target.value)}
                className={classNames(
                  "ml-auto rounded-md border px-2 py-1 text-xs font-semibold uppercase outline-none",
                  isAdminLight
                    ? "border-white/25 bg-[#2f3658] text-white"
                    : "border-[#2a3942] bg-[#111b21] text-[#e9edef]"
                )}
                aria-label="Selecteer thema"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto</option>
              </select>
            </label>
          </div>
        ) : null}
        {!sidebarCollapsed ? (
        <div
          className={classNames(
            "mt-4 text-center text-xs",
            isAdminLight ? "text-[#d7dcef]" : "text-[#8696a0]"
          )}
        >
      
    </div>
        ) : null}
        <button
          type="button"
          onClick={() => setSidebarCollapsed?.(!sidebarCollapsed)}
          className={classNames(
            "mt-3 inline-flex h-9 w-full items-center justify-center rounded-lg border transition-colors",
            isAdminLight
              ? "border-white/25 text-[#d7dcef] hover:bg-white/10 hover:text-white"
              : "border-[#2a3942] text-[#8696a0] hover:bg-[#2a3942] hover:text-[#e9edef]"
          )}
          title={sidebarCollapsed ? "Sidebar uitklappen" : "Sidebar inklappen"}
          aria-label={sidebarCollapsed ? "Sidebar uitklappen" : "Sidebar inklappen"}
        >
          {sidebarCollapsed ? (
            <ChevronRightIcon className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronLeftIcon className="h-4 w-4" aria-hidden />
          )}
        </button>

        <div className="mt-16 text-center text-xs">
          <p className="text-gray-400">Version {APP_VERSION}</p>
        </div>
      </nav>
    </div>
  );
};

export default SideBar;
